import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import pilotService from '../services/pilotService';

const PilotAccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('validating'); // validating, success, error
  const [error, setError] = useState('');
  const [pilotInfo, setPilotInfo] = useState(null);

  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setError('No access token provided. Please use the link from your invitation email.');
        return;
      }

      try {
        const result = await pilotService.validateToken(token);
        if (result.success) {
          setStatus('success');
          setPilotInfo(result.pilot);
          // Redirect to login page after a short delay
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setStatus('error');
          setError('Invalid or expired access token.');
        }
      } catch (err) {
        setStatus('error');
        if (err.response?.data?.code === 'INVALID_TOKEN') {
          setError('This access link is invalid or has expired. Please request a new invitation.');
        } else {
          setError(err.response?.data?.error || 'Failed to validate access token. Please try again.');
        }
      }
    };

    validateToken();
  }, [searchParams, navigate]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo/Brand */}
        <div style={styles.logoContainer}>
          <div style={styles.logo}>
            <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" style={styles.logoIcon} />
            <span style={styles.logoText}>NivasaPro.ai</span>
          </div>
        </div>

        {status === 'validating' && (
          <div style={styles.content}>
            <div style={styles.spinner}></div>
            <h2 style={styles.title}>Validating Your Access</h2>
            <p style={styles.subtitle}>Please wait while we verify your invitation...</p>
          </div>
        )}

        {status === 'success' && (
          <div style={styles.content}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.title}>Welcome to the Pilot Program!</h2>
            {pilotInfo && (
              <p style={styles.subtitle}>
                Hello{pilotInfo.name ? `, ${pilotInfo.name}` : ''}! Your access has been granted.
              </p>
            )}
            <p style={styles.redirectText}>
              Redirecting you to login...
            </p>
            <div style={styles.progressBar}>
              <div style={styles.progressFill}></div>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div style={styles.content}>
            <div style={styles.errorIcon}>✕</div>
            <h2 style={styles.title}>Access Denied</h2>
            <p style={styles.errorText}>{error}</p>
            <button
              style={styles.button}
              onClick={() => navigate('/')}
            >
              Return to Home
            </button>
          </div>
        )}
      </div>

      {/* Background decoration */}
      <div style={styles.bgDecoration}></div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0f172a',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden'
  },
  card: {
    backgroundColor: '#1e293b',
    borderRadius: '16px',
    padding: '48px',
    maxWidth: '480px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    border: '1px solid #334155',
    position: 'relative',
    zIndex: 1
  },
  logoContainer: {
    marginBottom: '32px'
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px'
  },
  logoIcon: {
    width: '32px',
    height: '32px'
  },
  logoText: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#f8fafc',
    letterSpacing: '-0.5px'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #334155',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '24px'
  },
  successIcon: {
    width: '64px',
    height: '64px',
    backgroundColor: '#059669',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    color: 'white',
    marginBottom: '24px'
  },
  errorIcon: {
    width: '64px',
    height: '64px',
    backgroundColor: '#dc2626',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    color: 'white',
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#f8fafc',
    marginBottom: '12px',
    margin: '0 0 12px 0'
  },
  subtitle: {
    fontSize: '16px',
    color: '#94a3b8',
    margin: '0 0 8px 0'
  },
  redirectText: {
    fontSize: '14px',
    color: '#64748b',
    marginTop: '16px',
    marginBottom: '16px'
  },
  progressBar: {
    width: '200px',
    height: '4px',
    backgroundColor: '#334155',
    borderRadius: '2px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#3b82f6',
    animation: 'progress 3s linear forwards'
  },
  errorText: {
    fontSize: '16px',
    color: '#f87171',
    marginBottom: '24px',
    lineHeight: '1.5'
  },
  button: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '12px 32px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  bgDecoration: {
    position: 'absolute',
    top: '-50%',
    right: '-25%',
    width: '600px',
    height: '600px',
    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
    borderRadius: '50%',
    pointerEvents: 'none'
  }
};

// Add keyframes for animations via style tag
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes progress {
    from { width: 0%; }
    to { width: 100%; }
  }
`;
document.head.appendChild(styleSheet);

export default PilotAccess;
